import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

import { showToast, ToastType } from '@/helpers/toast';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { offlineDb, StoredEvent, StoredPlaylist, StoredSong } from '@/services/offlineDb';
import { Asset, OfflineEventMeta, Playlist } from '@/types';

export interface DownloadProgress {
  current: number;
  total: number;
}

export interface OfflineContextType {
  isOnline: boolean;

  // Download actions
  downloadSong: (asset: Asset, event: OfflineEventMeta) => Promise<void>;
  downloadPlaylist: (playlist: Playlist) => Promise<void>;

  // Remove actions
  removeOfflinePlaylist: (playlistId: number) => Promise<void>;
  removeOfflineSong: (assetId: number, eventId: number) => Promise<void>;
  removeOfflineEventWithSongs: (eventId: number) => Promise<void>;

  // Download state
  downloadedSongIds: Set<number>;
  downloadedPlaylistIds: Set<number>;
  downloadProgress: Record<number, DownloadProgress>;
  isDownloading: boolean;

  // Offline data
  offlinePlaylists: StoredPlaylist[];
  offlineEvents: StoredEvent[];
  getOfflinePlaylist: (id: number) => StoredPlaylist | undefined;
  getOfflineSongsForEvent: (eventId: number) => StoredEvent['songs'];

  // Playback
  getAudioBlobUrl: (assetId: number) => Promise<string | null>;
}

export const OfflineContext = createContext<OfflineContextType>({} as OfflineContextType);

export const useOfflineContext = () => useContext(OfflineContext);

export const OfflineProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isOnline = useOnlineStatus();

  const [offlinePlaylists, setOfflinePlaylists] = useState<StoredPlaylist[]>([]);
  const [offlineEvents, setOfflineEvents] = useState<StoredEvent[]>([]);
  const [downloadedSongIds, setDownloadedSongIds] = useState<Set<number>>(new Set());
  const [downloadedPlaylistIds, setDownloadedPlaylistIds] = useState<Set<number>>(new Set());
  const [downloadProgress, setDownloadProgress] = useState<Record<number, DownloadProgress>>({});

  const blobUrlCache = useRef<Map<number, string>>(new Map());

  const isDownloading = Object.keys(downloadProgress).length > 0;

  const loadOfflineData = useCallback(async () => {
    const [playlists, events, songs] = await Promise.all([
      offlineDb.getAllPlaylists(),
      offlineDb.getAllEvents(),
      offlineDb.getAllSongs()
    ]);
    setOfflinePlaylists(playlists);
    setOfflineEvents(events);
    setDownloadedSongIds(new Set(songs.map((s) => s.id)));
    setDownloadedPlaylistIds(new Set(playlists.map((p) => p.id)));
  }, []);

  useEffect(() => {
    loadOfflineData();
    return () => {
      blobUrlCache.current.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [loadOfflineData]);

  const fetchAudioBlob = async (url: string, onProgress: (pct: number) => void): Promise<Blob> => {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Fetch failed: ${response.status}`);
    }

    const contentLength = response.headers.get('Content-Length');
    if (contentLength && response.body) {
      const total = parseInt(contentLength, 10);
      const reader = response.body.getReader();
      const chunks: Uint8Array[] = [];
      let received = 0;

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }
        chunks.push(value);
        received += value.length;
        onProgress(Math.round((received / total) * 100));
      }

      const mimeType = response.headers.get('Content-Type') ?? 'audio/mpeg';
      return new Blob(chunks as unknown as BlobPart[], { type: mimeType });
    }

    onProgress(50);
    const blob = await response.blob();
    onProgress(100);
    return blob;
  };

  const downloadSong = useCallback(
    async (asset: Asset, event: OfflineEventMeta) => {
      if (!downloadedSongIds.has(asset.id)) {
        setDownloadProgress((prev) => ({
          ...prev,
          [asset.id]: { current: 0, total: 100 }
        }));

        try {
          const blob = await fetchAudioBlob(asset.assetUrl, (pct) => {
            setDownloadProgress((prev) => ({
              ...prev,
              [asset.id]: { current: pct, total: 100 }
            }));
          });

          const storedSong: StoredSong = {
            id: asset.id,
            assetName: asset.assetName,
            duration: asset.duration,
            blob,
            downloadedAt: Date.now()
          };

          await offlineDb.putSong(storedSong);
          setDownloadedSongIds((prev) => new Set([...prev, asset.id]));
        } finally {
          setDownloadProgress((prev) => {
            const next = { ...prev };
            delete next[asset.id];
            return next;
          });
        }
      }

      const songMeta = { id: asset.id, assetName: asset.assetName, duration: asset.duration };
      const existingEvent = await offlineDb.getEvent(event.id);

      if (existingEvent) {
        if (!existingEvent.songs.some((s) => s.id === asset.id)) {
          await offlineDb.putEvent({
            ...existingEvent,
            songs: [...existingEvent.songs, songMeta]
          });
        }
      } else {
        await offlineDb.putEvent({
          id: event.id,
          name: event.name,
          description: event.description,
          startDateAndTime: event.startDateAndTime,
          organizationName: event.organizationName,
          status: event.status,
          songs: [songMeta],
          downloadedAt: Date.now()
        });
      }

      await loadOfflineData();
      showToast(ToastType.SUCCESS, `"${asset.assetName}" is now available offline.`);
    },
    [downloadedSongIds, loadOfflineData]
  );

  const downloadPlaylist = useCallback(
    async (playlist: Playlist) => {
      const songMetas: StoredPlaylist['songs'] = [];

      for (const pa of playlist.playlistAssets) {
        const { asset, order } = pa;

        if (!downloadedSongIds.has(asset.id)) {
          setDownloadProgress((prev) => ({
            ...prev,
            [asset.id]: { current: 0, total: 100 }
          }));

          try {
            const blob = await fetchAudioBlob(asset.assetUrl, (pct) => {
              setDownloadProgress((prev) => ({
                ...prev,
                [asset.id]: { current: pct, total: 100 }
              }));
            });

            await offlineDb.putSong({
              id: asset.id,
              assetName: asset.assetName,
              duration: asset.duration,
              blob,
              downloadedAt: Date.now()
            });
          } catch (songError) {
            // Continue processing playlist even if one song fails.
            showToast(ToastType.ERROR, `Failed to download "${asset.assetName}" for offline use.`);
          } finally {
            setDownloadProgress((prev) => {
              const next = { ...prev };
              delete next[asset.id];
              return next;
            });
          }
        }

        songMetas.push({
          id: asset.id,
          assetName: asset.assetName,
          duration: asset.duration,
          order
        });
      }

      await offlineDb.putPlaylist({
        id: playlist.id,
        name: playlist.name,
        description: playlist.description,
        organizationId: playlist.organizationId,
        totalDuration: playlist.totalDuration,
        songs: songMetas.sort((a, b) => a.order - b.order),
        downloadedAt: Date.now()
      });

      await loadOfflineData();
      showToast(
        ToastType.SUCCESS,
        `"${playlist.name}" downloaded! ${playlist.playlistAssets.length} songs available offline.`
      );
    },
    [downloadedSongIds, loadOfflineData]
  );

  const removeOfflinePlaylist = useCallback(
    async (playlistId: number) => {
      const storedPlaylist = await offlineDb.getPlaylist(playlistId);
      if (!storedPlaylist) {
        return;
      }

      const playlistSongIds = Array.from(new Set(storedPlaylist.songs.map((song) => song.id)));
      await offlineDb.deletePlaylist(playlistId);

      const [allPlaylists, allEvents] = await Promise.all([
        offlineDb.getAllPlaylists(),
        offlineDb.getAllEvents()
      ]);

      await Promise.all(
        playlistSongIds.map(async (songId) => {
          const usedElsewhere =
            allPlaylists.some((p) => p.songs.some((s) => s.id === songId)) ||
            allEvents.some((e) => e.songs.some((s) => s.id === songId));

          if (!usedElsewhere) {
            const cachedUrl = blobUrlCache.current.get(songId);
            if (cachedUrl) {
              URL.revokeObjectURL(cachedUrl);
              blobUrlCache.current.delete(songId);
            }
            await offlineDb.deleteSong(songId);
          }
        })
      );

      await loadOfflineData();
    },
    [loadOfflineData]
  );

  const removeOfflineSong = useCallback(
    async (assetId: number, eventId: number) => {
      const storedEvent = await offlineDb.getEvent(eventId);
      if (storedEvent) {
        const updatedSongs = storedEvent.songs.filter((s) => s.id !== assetId);
        if (updatedSongs.length === 0) {
          await offlineDb.deleteEvent(eventId);
        } else {
          await offlineDb.putEvent({ ...storedEvent, songs: updatedSongs });
        }
      }
      const allPlaylists = await offlineDb.getAllPlaylists();
      const allEvents = await offlineDb.getAllEvents();
      const usedElsewhere =
        allPlaylists.some((p) => p.songs.some((s) => s.id === assetId)) ||
        allEvents.some((e) => e.id !== eventId && e.songs.some((s) => s.id === assetId));

      if (!usedElsewhere) {
        blobUrlCache.current.delete(assetId);
        await offlineDb.deleteSong(assetId);
      }

      await loadOfflineData();
    },
    [loadOfflineData]
  );

  const removeOfflineEventWithSongs = useCallback(
    async (eventId: number) => {
      const storedEvent = await offlineDb.getEvent(eventId);
      if (!storedEvent) {
        return;
      }

      const eventSongIds = Array.from(new Set(storedEvent.songs.map((song) => song.id)));
      await offlineDb.deleteEvent(eventId);

      const [allPlaylists, allEvents] = await Promise.all([
        offlineDb.getAllPlaylists(),
        offlineDb.getAllEvents()
      ]);

      await Promise.all(
        eventSongIds.map(async (songId) => {
          const usedElsewhere =
            allPlaylists.some((p) => p.songs.some((s) => s.id === songId)) ||
            allEvents.some((e) => e.songs.some((s) => s.id === songId));

          if (!usedElsewhere) {
            const cachedUrl = blobUrlCache.current.get(songId);
            if (cachedUrl) {
              URL.revokeObjectURL(cachedUrl);
              blobUrlCache.current.delete(songId);
            }
            await offlineDb.deleteSong(songId);
          }
        })
      );

      await loadOfflineData();
    },
    [loadOfflineData]
  );

  const getOfflinePlaylist = useCallback(
    (id: number) => offlinePlaylists.find((p) => p.id === id),
    [offlinePlaylists]
  );

  const getOfflineSongsForEvent = useCallback(
    (eventId: number) => offlineEvents.find((e) => e.id === eventId)?.songs ?? [],
    [offlineEvents]
  );

  const getAudioBlobUrl = useCallback(async (assetId: number): Promise<string | null> => {
    const cached = blobUrlCache.current.get(assetId);
    if (cached) {
      return cached;
    }

    const song = await offlineDb.getSong(assetId);
    if (!song) {
      return null;
    }

    // Ensure proper audio MIME type for Safari compatibility
    // Safari is strict about blob MIME types for audio playback
    const blob =
      song.blob.type && song.blob.type !== 'application/octet-stream'
        ? song.blob
        : new Blob([song.blob], { type: 'audio/mpeg' });

    const url = URL.createObjectURL(blob);
    blobUrlCache.current.set(assetId, url);
    return url;
  }, []);

  return (
    <OfflineContext.Provider
      value={{
        isOnline,
        downloadSong,
        downloadPlaylist,
        removeOfflinePlaylist,
        removeOfflineSong,
        removeOfflineEventWithSongs,
        downloadedSongIds,
        downloadedPlaylistIds,
        downloadProgress,
        isDownloading,
        offlinePlaylists,
        offlineEvents,
        getOfflinePlaylist,
        getOfflineSongsForEvent,
        getAudioBlobUrl
      }}>
      {children}
    </OfflineContext.Provider>
  );
};
